import {
  BarItem,
  Bar,
  type BarCustomLayer as BarCustomLayerFn,
  type BarDatum,
  type BarItemProps,
} from "@nivo/bar";
import { Axes } from "@nivo/axes";
import { MotionConfigProvider } from "@nivo/core";
import { line, curveMonotoneX } from "d3-shape";
import { useMemo, type ReactNode } from "react";

import {
  BAR_COLOR_ORDER,
  EMPTY_HATCH_CSS,
  EMPTY_HATCH_DEF,
  isBarsOfParts,
  isEmptyGroup,
  LINE_COLOR_ORDER,
} from ".";

import { ChartData } from "@/__generated__/graphql";
import {
  AXIS_FONT_SIZE,
  fittingAxisLabels,
  useAnimatesOnChange,
  useFittedValueAxis,
  usePlotSize,
  formatLabel,
  AXIS_NAME_ROOM,
  formatValue,
  toBarData,
} from "@/components/organisms/Chart/Chart.utils";
import { ChartTooltip } from "@/components/organisms/Chart/ChartTooltip";

import { typographyStyles } from "@/components/ui/typography";

// Nivo's motion is context, and the context's provider is shipped without a
// type: @nivo/core is typed by a hand-written index.d.ts that stops at the
// hook which reads it. Named here so the axes can be drawn outside the
// chart's own motion (see ``instantAxes`` below).
declare module "@nivo/core" {
  export const MotionConfigProvider: (props: {
    animate?: boolean;
    config?: string;
    children?: ReactNode;
  }) => ReactNode;
}

/** Where a tick sits, and what it says - all either axis here draws with. */
type AxisTick = { x: number; y: number; value: string | number | Date };

/**
 * A bar that only ever moves up and down.
 *
 * Nivo animates a bar's whole ``transform`` and its width, so a chart whose
 * columns change - a different period, one more account - slides its bars
 * sideways while they grow. Sideways is noise: where a bar sits along the axis
 * is not a quantity, it is which thing the bar is, and it should simply be
 * there. So x and width are drawn as they are, and only the height is
 * animated - with the top edge following from it, which is what makes a bar
 * grow out of its own baseline rather than drift to it.
 */
function VerticalOnlyBar<RawDatum extends BarDatum>(
  props: BarItemProps<RawDatum>,
) {
  const { bar, style } = props;
  // A bar hangs off the zero line: upwards from its foot, or downwards from
  // its top when what it shows is negative.
  const hangsDown = Number(bar.data.value ?? 0) < 0;
  const foot = bar.y + bar.height;
  const transform = style.height.to((height) => {
    const grown = Math.max(Number(height), 0);
    return `translate(${bar.x}, ${hangsDown ? bar.y : foot - grown})`;
  });

  return (
    <BarItem
      {...props}
      style={{
        ...style,
        width: bar.width as unknown as (typeof style)["width"],
        transform: transform as unknown as (typeof style)["transform"],
      }}
    />
  );
}

// The bottom axis draws its labels flat, 16px under the axis, so it needs that
// plus the text's own height - not the deep gutter a rotated label wants. The
// left margin is worked out per chart from the numbers it carries.
const SIZE_MARGINS = {
  xs: { top: 30, right: 8, bottom: 20, left: 32 },
  sm: { top: 32, right: 10, bottom: 22, left: 34 },
  md: { top: 34, right: 12, bottom: 24, left: 36 },
  lg: { top: 38, right: 16, bottom: 26, left: 38 },
};

const SIZE_CONFIGS = {
  xs: {
    padding: 0.2,
    innerPadding: 2,
    borderRadius: 4,
    tickValues: 4,
  },
  sm: {
    padding: 0.2,
    innerPadding: 3,
    borderRadius: 5,
    tickValues: 5,
  },
  md: {
    padding: 0.2,
    innerPadding: 3,
    borderRadius: 6,
    tickValues: 6,
  },
  lg: {
    padding: 0.2,
    innerPadding: 4,
    borderRadius: 7,
    tickValues: 7,
  },
};

export function BarChart({
  data,
  size = "md",
  lineKeys = [],
  colourOf,
}: {
  data: ChartData;
  size?: "xs" | "sm" | "md" | "lg";
  lineKeys?: string[];
  /** What each bar is drawn in, by the name it stands for. */
  colourOf?: (label: string) => string;
}) {
  const {
    data: barData,
    keys: allKeys,
    indexBy,
  } = useMemo(() => toBarData(data), [data]);
  const barKeys = useMemo(
    () => allKeys.filter((k) => !lineKeys.includes(k)),
    [allKeys, lineKeys],
  );
  const isStacked = lineKeys.length > 0;
  const sizeConfig = SIZE_CONFIGS[size];

  const {
    ref: wrapperRef,
    width: plotWidth,
    height: plotHeight,
  } = usePlotSize();

  // Wide enough for the numbers the value axis turned out to carry.
  const valueGutter = useFittedValueAxis(wrapperRef, barData);
  const margins = useMemo(() => {
    const base = SIZE_MARGINS[size];
    // Room for the words beside each axis, on top of the room the numbers
    // themselves need.
    return {
      ...base,
      left:
        Math.max(base.left, valueGutter) +
        (data.valueAxis ? AXIS_NAME_ROOM : 0),
      bottom: base.bottom + (data.labelAxis ? AXIS_NAME_ROOM : 0),
    };
  }, [size, valueGutter, data.labelAxis, data.valueAxis]);

  const animates = useAnimatesOnChange(data);

  // Which of this chart's own names stand for rows the grouping says nothing
  // about - a series, a column, or both.
  const emptyNames = useMemo(
    () =>
      [
        ...new Set([...allKeys, ...barData.map((row) => String(row[indexBy]))]),
      ].filter((name) => isEmptyGroup(name)),
    [allKeys, barData, indexBy],
  );

  const bottomTickValues = useMemo(
    () =>
      fittingAxisLabels(
        barData.map((row) => String(row[indexBy])),
        data.labelFormat,
        Math.max(plotWidth - margins.left - margins.right, 0),
      ),
    [
      barData,
      data.labelFormat,
      indexBy,
      margins.left,
      margins.right,
      plotWidth,
    ],
  );

  // Every series flat at zero — a period with no movement. Nivo's auto scale
  // collapses to a degenerate domain there and paints the baseline through the
  // middle of an empty plot, which reads as broken rather than as empty.
  const isFlat = useMemo(
    () =>
      barData.length > 0 &&
      barData.every((row) =>
        allKeys.every((key) => {
          const value = row[key] as number | undefined;
          return value == null || value === 0;
        }),
      ),
    [allKeys, barData],
  );

  const scaleMin = useMemo(() => {
    if (isFlat) return 0;
    if (lineKeys.length === 0) return "auto" as const;
    let min = 0;
    for (const d of barData) {
      for (const k of lineKeys) {
        const v = d[k] as number;
        if (v != null && v < min) min = v;
      }
    }
    return min < 0 ? min : ("auto" as const);
  }, [barData, isFlat, lineKeys]);

  const scaleMax = useMemo(() => {
    if (isFlat) return 1;
    if (lineKeys.length === 0) return "auto" as const;
    let max = 0;
    for (const d of barData) {
      for (const k of lineKeys) {
        const v = d[k] as number;
        if (v != null && v > max) max = v;
      }
    }
    return max > 0 ? max : ("auto" as const);
  }, [barData, isFlat, lineKeys]);

  // The value axis and the label axis, held here rather than handed to the
  // chart as props, because the layer below is what draws them.
  const valueAxis = {
    legend: data.valueAxis || undefined,
    legendPosition: "middle",
    legendOffset: -(margins.left - 10),
    tickSize: 0,
    tickPadding: 8,
    tickValues: isFlat ? [0] : sizeConfig.tickValues,
    renderTick: ({ x, y, value }: AxisTick) => (
      <g transform={`translate(${x},${y})`}>
        <text
          data-axis="value"
          x={-8}
          y={0}
          dy={4}
          textAnchor="end"
          fontFamily={typographyStyles["body-xs"].fontFamily as string}
          fontSize={AXIS_FONT_SIZE}
          fill={"color-mix(in oklab, var(--muted-foreground) 80%, transparent)"}
        >
          {formatValue(value as number, data.valueFormat)}
        </text>
      </g>
    ),
  };

  const labelAxis = {
    // What the axis is of, said once beside it rather than left for a reader
    // to work out from the ticks.
    legend: data.labelAxis || undefined,
    legendPosition: "middle",
    // As far out as the room made for it, so it clears the ticks however far
    // they lean.
    legendOffset: margins.bottom - 6,
    tickSize: 0,
    tickPadding: 8,
    tickValues: bottomTickValues,
    renderTick: ({ x, y, value }: AxisTick) => (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="middle"
          fontFamily={typographyStyles["body-xs"].fontFamily as string}
          fontSize={AXIS_FONT_SIZE}
          fill={"color-mix(in oklab, var(--muted-foreground) 80%, transparent)"}
        >
          {formatLabel(String(value), data.labelFormat)}
        </text>
      </g>
    ),
  };

  /**
   * The axes, drawn without animation.
   *
   * Nivo fades a departing tick out while the arriving one fades in, so for as
   * long as a chart animates - and it should animate, that is the bars growing
   * into their new numbers - every name along the axis is on screen twice, one
   * set painted over the other. A name is not a quantity: there is nothing to
   * follow from the old one to the new one, so the axes are drawn outside the
   * chart's own motion and simply change.
   */
  const instantAxes: BarCustomLayerFn<any> = ({
    xScale,
    yScale,
    innerWidth,
    innerHeight,
  }) => (
    <MotionConfigProvider animate={false}>
      <Axes
        xScale={xScale as never}
        yScale={yScale as never}
        width={innerWidth}
        height={innerHeight}
        bottom={labelAxis as never}
        left={valueAxis as never}
      />
    </MotionConfigProvider>
  );

  const lineOverlayLayer: BarCustomLayerFn<any> = ({
    bars,
    xScale,
    yScale,
    innerWidth,
  }) => {
    if (lineKeys.length === 0) return null;

    const bandWidth = (xScale as any).bandwidth?.() ?? 0;

    return (
      <g>
        {lineKeys.map((lineKey, lineIdx) => {
          const labelPositions = new Map<string, number>();
          bars.forEach((bar: any) => {
            const label = bar.data.indexValue;
            if (!labelPositions.has(String(label))) {
              labelPositions.set(String(label), bar.x + bandWidth / 2);
            }
          });

          const points: [number, number][] = barData
            .map((d) => {
              const xPos = labelPositions.get(String(d[indexBy]));
              const val = d[lineKey] as number;
              if (xPos == null || val == null) return null;
              return [xPos, (yScale as any)(val) as number] as [number, number];
            })
            .filter((p): p is [number, number] => p != null);

          const pathGenerator = line<[number, number]>()
            .x((d) => d[0])
            .y((d) => d[1])
            .curve(curveMonotoneX);

          const path = pathGenerator(points);
          if (!path) return null;

          const color = LINE_COLOR_ORDER[lineIdx % LINE_COLOR_ORDER.length];

          return (
            <g key={lineKey}>
              <path
                d={path}
                fill="none"
                stroke={color}
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              {points.map(([px, py], i) => (
                <circle key={i} cx={px} cy={py} r={3} fill={color} />
              ))}
            </g>
          );
        })}
      </g>
    );
  };

  return (
    <div
      ref={wrapperRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
    >
      {plotWidth > 0 && plotHeight > 0 && (
        <Bar
          width={plotWidth}
          height={plotHeight}
          isInteractive={true}
          animate={animates}
          barComponent={VerticalOnlyBar}
          groupMode={isStacked ? "stacked" : "grouped"}
          layout="vertical"
          data={barData}
          keys={barKeys}
          indexBy={indexBy}
          margin={margins}
          padding={sizeConfig.padding}
          enableGridX={false}
          enableGridY={true}
          gridYValues={isFlat ? [0] : sizeConfig.tickValues}
          colors={
            colourOf
              ? // A bar per category is a thing of its own, coloured as a pie's
                // slices are - by which thing it is, so putting one away leaves
                // the rest as they were. A bar per month is the same thing
                // again, so the colour stays put and only the height moves.
                ({ indexValue }: { indexValue: string | number }) =>
                  colourOf(String(indexValue))
              : BAR_COLOR_ORDER
          }
          // A bar for rows the grouping says nothing about is struck in grey
          // rather than coloured, whether the empty group is the series it
          // belongs to or the column it stands in.
          defs={[EMPTY_HATCH_DEF]}
          fill={emptyNames.flatMap((name) => [
            { match: { id: name }, id: EMPTY_HATCH_DEF.id },
            { match: { indexValue: name }, id: EMPTY_HATCH_DEF.id },
          ])}
          valueScale={{ type: "linear", min: scaleMin, max: scaleMax }}
          innerPadding={isStacked ? 0 : sizeConfig.innerPadding}
          borderRadius={sizeConfig.borderRadius}
          enableLabel={false}
          enableTotals={false}
          layers={[
            "grid",
            instantAxes,
            "bars",
            lineOverlayLayer,
            "markers",
            "legends",
            "annotations",
          ]}
          tooltip={(datum) => {
            return (
              <ChartTooltip
                values={allKeys}
                datum={datum}
                size={size}
                colors={
                  isBarsOfParts("bar", data)
                    ? // One row, for the bar under the pointer: its own colour,
                      // not the first of the order.
                      [
                        isEmptyGroup(datum.data[indexBy])
                          ? EMPTY_HATCH_CSS
                          : datum.color,
                      ]
                    : [
                        ...BAR_COLOR_ORDER.slice(0, barKeys.length),
                        ...LINE_COLOR_ORDER.slice(0, lineKeys.length),
                      ].map((color, index) =>
                        isEmptyGroup(allKeys[index]) ? EMPTY_HATCH_CSS : color,
                      )
                }
                checkIsActiveValue={(innerDatum: any) =>
                  datum?.data?.[indexBy] === innerDatum?.data?.[indexBy]
                }
                keyColumnHeader={formatLabel(
                  String(datum.data[indexBy]),
                  data.labelFormat,
                )}
                valueColumnHeader="Value"
                renderKeyColumn={(_, value: string) => value}
                renderValueColumn={(innerDatum: any, value: string) =>
                  formatValue(innerDatum?.data?.[value], data.valueFormat)
                }
              />
            );
          }}
          theme={{
            grid: {
              line: { stroke: "var(--border)" },
            },
            axis: {
              domain: { line: { stroke: "var(--border)" } },
              ticks: { line: { stroke: "transparent" } },
              legend: {
                text: {
                  // The axis's name is set in the app's own type, not in the
                  // chart library's default sans: a chart with two typefaces in
                  // it reads as two charts.
                  fontFamily: typographyStyles["body-sm"].fontFamily as string,
                  fontSize: typographyStyles["body-sm"].fontSize as string,
                  fontWeight: 600,
                  fill: "color-mix(in oklab, var(--muted-foreground) 80%, transparent)",
                },
              },
            },
          }}
          role="application"
        />
      )}
    </div>
  );
}
